import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import {
  type LogisticsDhlAddressInput,
  LogisticsDhlSetting,
  LogisticsDhlShippingSettingsDocument,
  type LogisticsDhlShippingSettingsQuery,
  UpdateLogisticsDhlShippingSettingsDocument,
  type UpdateLogisticsDhlShippingSettingsMutation,
  type UpdateLogisticsDhlShippingSettingsMutationVariables,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { dhlSettingLabelKey, invalidDhlSettings, writeDhlShippingSettings } from "../../lib/logisticsDhlSettings";

type Settings = LogisticsDhlShippingSettingsQuery["logisticsDhlShippingSettings"];
type FormValues = {
  shipper: LogisticsDhlAddressInput;
  returnAddress: LogisticsDhlAddressInput;
  labelPrintFormat: string;
  returnLabelPrintFormat: string;
};

const addressValues = (address: Settings["shipper"]): LogisticsDhlAddressInput => ({
  name: address?.name ?? "",
  street: address?.street ?? "",
  postalCode: address?.postalCode ?? "",
  city: address?.city ?? "",
});

const formValues = (settings: Settings): FormValues => ({
  shipper: addressValues(settings.shipper),
  returnAddress: addressValues(settings.returnAddress),
  labelPrintFormat: settings.labelPrintFormat,
  returnLabelPrintFormat: settings.returnLabelPrintFormat,
});

const addressFields: {
  key: keyof LogisticsDhlAddressInput;
  shipper: LogisticsDhlSetting;
  returnAddress: LogisticsDhlSetting;
}[] = [
  { key: "name", shipper: LogisticsDhlSetting.ShipperName, returnAddress: LogisticsDhlSetting.ReturnName },
  { key: "street", shipper: LogisticsDhlSetting.ShipperStreet, returnAddress: LogisticsDhlSetting.ReturnStreet },
  {
    key: "postalCode",
    shipper: LogisticsDhlSetting.ShipperPostalCode,
    returnAddress: LogisticsDhlSetting.ReturnPostalCode,
  },
  { key: "city", shipper: LogisticsDhlSetting.ShipperCity, returnAddress: LogisticsDhlSetting.ReturnCity },
];

export const DhlShippingSettings = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { data, loading, error } = useQuery<LogisticsDhlShippingSettingsQuery>(LogisticsDhlShippingSettingsDocument, {
    fetchPolicy: "network-only",
  });
  const [form, setForm] = useState<FormValues | null>(null);
  const [invalid, setInvalid] = useState<LogisticsDhlSetting[]>([]);
  const saveInFlight = useRef(false);
  const [saveSettings, { loading: saving }] = useMutation<
    UpdateLogisticsDhlShippingSettingsMutation,
    UpdateLogisticsDhlShippingSettingsMutationVariables
  >(UpdateLogisticsDhlShippingSettingsDocument, {
    update: (cache, { data: mutationData }) => {
      const saved = mutationData?.updateLogisticsDhlShippingSettings;
      if (!saved) return;
      writeDhlShippingSettings(cache, saved);
    },
  });
  const settings = data?.logisticsDhlShippingSettings;

  useEffect(() => {
    if (!loading && !error && settings && form === null) setForm(formValues(settings));
  }, [loading, error, settings, form]);

  const updateForm = (setting: LogisticsDhlSetting, change: Partial<FormValues>) => {
    setInvalid(current => current.filter(field => field !== setting));
    setForm(current => current && { ...current, ...change });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form || loading || error || saving || saveInFlight.current) return;
    saveInFlight.current = true;
    setInvalid([]);
    try {
      const result = await saveSettings({
        variables: {
          input: {
            ...form,
            returnAddress: Object.values(form.returnAddress).every(value => value.trim() === "")
              ? null
              : form.returnAddress,
          },
        },
      });
      const saved = result.data?.updateLogisticsDhlShippingSettings;
      if (!saved) throw new Error();
      setForm(formValues(saved));
      notify("dhlSettings.saved", { type: "success" });
    } catch (saveError) {
      const fields = invalidDhlSettings(saveError);
      setInvalid(fields);
      if (fields.length === 0)
        captureException(new Error("DHL shipping settings save failed"), "logistics/dhl: save shipping settings");
      notify(translate(fields.length ? "dhlSettings.invalid" : "dhlSettings.saveError"), { type: "error" });
    } finally {
      saveInFlight.current = false;
    }
  };

  if (error) return <Alert severity="error">{translate("dhlSettings.loadError")}</Alert>;
  if (loading || !settings || !form) return <CircularProgress aria-label={translate("dhlSettings.loading")} />;

  const fieldError = (setting: LogisticsDhlSetting) => ({
    error: invalid.includes(setting),
    helperText: invalid.includes(setting) ? translate("dhlSettings.fieldInvalid") : undefined,
  });

  return (
    <Card>
      <CardContent>
        <Typography component="h2" variant="h6" gutterBottom>
          {translate("dhlSettings.shippingTitle")}
        </Typography>
        <Typography color="text.secondary" paragraph>
          {translate("dhlSettings.shippingDescription")}
        </Typography>
        {settings.missingSettings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate("dhlSettings.missingSettings")}
            <Box component="ul" sx={{ mb: 0 }}>
              {settings.missingSettings.map(setting => (
                <li key={setting}>{translate(dhlSettingLabelKey(setting))}</li>
              ))}
            </Box>
          </Alert>
        )}
        <Box component="form" aria-label={translate("dhlSettings.shippingTitle")} onSubmit={handleSubmit} noValidate>
          {(["shipper", "returnAddress"] as const).map(section => (
            <Box key={section} sx={{ mb: 2 }}>
              <Typography component="h3" variant="subtitle1">
                {translate(`dhlSettings.${section}`)}
              </Typography>
              {section === "returnAddress" && (
                <Typography color="text.secondary">{translate("dhlSettings.returnHint")}</Typography>
              )}
              {addressFields.map(field => (
                <TextField
                  key={field.key}
                  fullWidth
                  margin="normal"
                  required={section === "shipper"}
                  disabled={saving}
                  label={translate(dhlSettingLabelKey(field[section]))}
                  value={form[section][field.key]}
                  onChange={event =>
                    updateForm(field[section], { [section]: { ...form[section], [field.key]: event.target.value } })
                  }
                  {...fieldError(field[section])}
                />
              ))}
            </Box>
          ))}
          {(
            [
              {
                key: "labelPrintFormat",
                setting: LogisticsDhlSetting.LabelPrintFormat,
                options: settings.labelPrintFormats,
              },
              {
                key: "returnLabelPrintFormat",
                setting: LogisticsDhlSetting.ReturnLabelPrintFormat,
                options: settings.returnLabelPrintFormats,
              },
            ] as const
          ).map(({ key, setting, options }) => (
            <TextField
              key={key}
              select
              fullWidth
              margin="normal"
              disabled={saving}
              label={translate(dhlSettingLabelKey(setting))}
              value={form[key]}
              onChange={event => updateForm(setting, { [key]: event.target.value })}
              {...fieldError(setting)}
            >
              {options.map(value => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          ))}
          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {translate(saving ? "dhlSettings.saving" : "dhlSettings.save")}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
